import { useState, useEffect, useCallback } from "react";
import { API } from "@/lib/constants";
import { toast } from "sonner";
import {
  Loader2, FileImage, Download, AlertCircle, CheckCircle2, Clock,
  RefreshCw, Wand2, ChevronDown, ChevronUp, Star, Trash2, Upload, Layers,
} from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Button } from "./ui/button";

/**
 * Cover Library — Phase 6.1 polish, May 2026.
 *
 * Discovery surface for past cover runs. Without this, the wizard's
 * variant grid only existed in modal state; once the operator closed
 * or refreshed, the covers became unfindable even though the PNG files
 * + Mongo audit rows were persisted.
 *
 * Statuses surfaced:
 *   - "complete" — finished cleanly, all variants on disk
 *   - "in_progress" — stub-row exists but SSE was cancelled mid-flight;
 *                     variants completed so far are still recoverable
 *   - "recovered_from_disk" — backfilled by /recover-orphans; row was
 *                             absent before the stub-row-on-start fix
 *
 * Each row expands to show the variant grid with preview + download.
 */
export default function CoverLibraryDrawer({ open, onOpenChange, workspaceId, onOpenCard, onMutated }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedRunId, setExpandedRunId] = useState(null);
  const [recovering, setRecovering] = useState(false);
  const [tab, setTab] = useState("runs"); // "runs" | "saved"

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/v2/cover-design/runs?limit=50`, { credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setRuns(j.runs || []);
    } catch (e) {
      console.error(e);
      toast.error(`Failed to load cover library: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchRuns();
  }, [open, fetchRuns]);

  const handleRecoverOrphans = async () => {
    setRecovering(true);
    try {
      const r = await fetch(`${API}/v2/cover-design/recover-orphans`, {
        method: "POST", credentials: "include",
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      toast.success(`Scanned ${j.scanned} folders, recovered ${j.recovered} orphan runs`);
      fetchRuns();
    } catch (e) {
      toast.error(`Recover failed: ${e.message}`);
    } finally {
      setRecovering(false);
    }
  };

  const deleteRun = async (runId) => {
    if (!window.confirm("Delete this entire cover run? It will be removed from the library.")) return;
    try {
      const r = await fetch(`${API}/v2/cover-design/runs/${runId}`, { method: "DELETE", credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success("Cover run deleted");
      fetchRuns();
    } catch (e) {
      toast.error(`Delete failed: ${e.message}`);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="cover-library-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <FileImage className="h-5 w-5 text-blue-700" />
            Cover Library
          </SheetTitle>
          <SheetDescription>
            Generated runs + a saved library of reusable front covers and back pages.
          </SheetDescription>
        </SheetHeader>

        <div className="flex items-center gap-1 mt-4 border-b border-slate-200">
          <button
            onClick={() => setTab("runs")}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition ${tab === "runs" ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}
            data-testid="cover-library-tab-runs"
          >
            Generated runs
          </button>
          <button
            onClick={() => setTab("saved")}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition flex items-center gap-1 ${tab === "saved" ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}
            data-testid="cover-library-tab-saved"
          >
            <Star className="h-3.5 w-3.5" /> Saved backgrounds
          </button>
        </div>

        {tab === "runs" && (
          <>
            <div className="flex items-center justify-between my-4">
              <div className="text-xs text-slate-600">
                {loading ? "Loading…" : `${runs.length} runs`}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleRecoverOrphans}
                  disabled={recovering}
                  title="Scan the cover output folder for runs that never got a DB row (legacy + cancelled-stream recovery)"
                  data-testid="cover-library-recover-btn"
                >
                  {recovering ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
                  Recover orphans
                </Button>
                <Button size="sm" variant="outline" onClick={fetchRuns} disabled={loading} data-testid="cover-library-refresh-btn">
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Refresh
                </Button>
              </div>
            </div>

            {!loading && runs.length === 0 && (
              <div className="text-sm text-slate-500 italic text-center py-12" data-testid="cover-library-empty">
                No cover runs yet. Open the wizard and pick the COVER_DESIGN card type to make one.
              </div>
            )}

            <div className="space-y-3">
              {runs.map((r) => (
                <CoverRunRow
                  key={r.run_id}
                  run={r}
                  expanded={expandedRunId === r.run_id}
                  onToggle={() => setExpandedRunId(expandedRunId === r.run_id ? null : r.run_id)}
                  onDeleteRun={() => deleteRun(r.run_id)}
                  boardCtx={{ workspaceId, onOpenCard, onOpenChange, onMutated }}
                />
              ))}
            </div>
          </>
        )}

        {tab === "saved" && <SavedBackgroundsTab boardCtx={{ workspaceId, onOpenCard, onOpenChange, onMutated }} />}
      </SheetContent>
    </Sheet>
  );
}

function CoverRunRow({ run, expanded, onToggle, onDeleteRun, boardCtx }) {
  const statusBadge = {
    complete: { label: "Complete", icon: CheckCircle2, cls: "bg-emerald-100 text-emerald-900 border-emerald-300" },
    in_progress: { label: "In progress", icon: Clock, cls: "bg-amber-100 text-amber-900 border-amber-300" },
    stream_failed: { label: "Stream failed", icon: AlertCircle, cls: "bg-red-100 text-red-900 border-red-300" },
    recovered_from_disk: { label: "Recovered from disk", icon: AlertCircle, cls: "bg-blue-100 text-blue-900 border-blue-300" },
  }[run.status] || { label: run.status || "unknown", icon: AlertCircle, cls: "bg-slate-100 text-slate-700 border-slate-300" };
  const BadgeIcon = statusBadge.icon;

  const totalVariants = (run.front_variant_count || 0) + (run.back_variant_count || 0);
  const thumbSide = run.first_front_variant_index != null ? "front" : (run.first_back_variant_index != null ? "back" : null);
  const thumbIdx = thumbSide === "front" ? run.first_front_variant_index : run.first_back_variant_index;
  const thumbSrc = thumbSide != null
    ? `${API}/v2/cover-design/runs/${run.run_id}/preview/${thumbSide}/${thumbIdx}`
    : null;

  const briefShort = (run.brief || "(no brief)").length > 90
    ? (run.brief || "").slice(0, 90) + "…"
    : (run.brief || "(no brief)");

  return (
    <div className="rounded-md border border-slate-200 bg-white" data-testid={`cover-library-run-${run.run_id}`}>
      <button
        onClick={onToggle}
        className="w-full p-3 flex items-start gap-3 text-left hover:bg-slate-50 transition-colors"
      >
        {thumbSrc ? (
          <img
            src={thumbSrc}
            alt={`${run.run_id} thumbnail`}
            className="w-16 h-20 object-cover rounded border border-slate-200 flex-shrink-0"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <div className="w-16 h-20 bg-slate-100 rounded border border-slate-200 flex items-center justify-center flex-shrink-0">
            <FileImage className="h-5 w-5 text-slate-400" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-[10px] px-1.5 py-0.5 rounded border flex items-center gap-1 ${statusBadge.cls}`}>
              <BadgeIcon className="h-3 w-3" />
              {statusBadge.label}
            </span>
            <span className="text-[10px] text-slate-500 font-mono">{run.orientation}</span>
            <span className="text-[10px] text-slate-500">{totalVariants} variants</span>
          </div>
          <div className="text-sm text-slate-800 leading-snug">{briefShort}</div>
          <div className="text-[10px] text-slate-500 font-mono mt-1">
            {run.run_id} · {new Date(run.created_at).toLocaleString()}
          </div>
        </div>
        {expanded ? <ChevronUp className="h-4 w-4 text-slate-400 flex-shrink-0 mt-1" /> : <ChevronDown className="h-4 w-4 text-slate-400 flex-shrink-0 mt-1" />}
      </button>

      <div className="flex items-center justify-end px-3 pb-2">
        <button
          type="button"
          onClick={onDeleteRun}
          className="text-[11px] text-red-600 hover:text-red-800 flex items-center gap-1"
          data-testid={`cover-library-delete-run-${run.run_id}`}
        >
          <Trash2 className="h-3 w-3" /> Delete run
        </button>
      </div>

      {expanded && (
        <ExpandedRunBody runId={run.run_id} boardCtx={boardCtx} />
      )}
    </div>
  );
}

function ExpandedRunBody({ runId, boardCtx }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const reload = useCallback(() => {
    setLoading(true);
    fetch(`${API}/v2/cover-design/runs/${runId}`, { credentials: "include" })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(setDetail)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [runId]);

  useEffect(() => { reload(); }, [reload]);

  if (loading) {
    return <div className="px-3 pb-3 text-xs text-slate-500 flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin" /> Loading variants…</div>;
  }
  if (error) {
    return <div className="px-3 pb-3 text-xs text-red-600">Failed to load: {error}</div>;
  }
  if (!detail) return null;

  return (
    <div className="px-3 pb-3 border-t border-slate-200 pt-3 space-y-3">
      {detail.front_variants?.length > 0 && (
        <VariantStrip runId={runId} side="front" variants={detail.front_variants} onChanged={reload} boardCtx={boardCtx} />
      )}
      {detail.back_variants?.length > 0 && (
        <VariantStrip runId={runId} side="back" variants={detail.back_variants} onChanged={reload} boardCtx={boardCtx} />
      )}
      {detail.rewritten_prompt && (
        <details className="text-[11px] text-slate-600">
          <summary className="cursor-pointer">Rewritten prompt</summary>
          <pre className="mt-2 p-2 bg-slate-50 border border-slate-200 rounded whitespace-pre-wrap font-mono text-[10px]">
            {detail.rewritten_prompt}
          </pre>
        </details>
      )}
    </div>
  );
}

function VariantStrip({ runId, side, variants, onChanged, boardCtx }) {
  return (
    <div data-testid={`cover-library-variants-${side}-${runId}`}>
      <div className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide mb-1">{side}</div>
      <div className="grid grid-cols-3 gap-2">
        {variants.map(v => (
          <CoverLibraryVariantTile
            key={v.variant_index}
            runId={runId}
            side={side}
            variantIndex={v.variant_index}
            onChanged={onChanged}
            boardCtx={boardCtx}
          />
        ))}
      </div>
    </div>
  );
}

// Create an IN_DESIGN planner card from a cover/background and open the full
// editor (ImageOverlayPanel) so text/logos can be repositioned.
async function sendCoverToBoard({ boardCtx, title, headline, subheader, bridge, setBusy }) {
  const { workspaceId, onOpenCard, onOpenChange, onMutated } = boardCtx || {};
  if (!workspaceId) { toast.error("No workspace loaded — reopen the planner and try again"); return; }
  setBusy && setBusy(true);
  const tId = toast.loading("Adding to planner & opening editor…");
  try {
    const cr = await fetch(`${API}/v2/workspaces/${workspaceId}/cards`, {
      method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        title, category: "COMMERCIAL", card_type: "COVER_DESIGN",
        graphic_headline: headline || null, graphic_subheader: subheader || null,
        body_md: "", initial_stage: "IN_DESIGN",
      }),
    });
    if (!cr.ok) { const e = await cr.json().catch(() => ({})); throw new Error(e?.detail || `Create failed (${cr.status})`); }
    const card = (await cr.json()).card;
    const br = await fetch(`${API}/v2/workspaces/${workspaceId}/cards/${card.card_id}/image/from-cover`, {
      method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(bridge),
    });
    if (!br.ok) { const e = await br.json().catch(() => ({})); throw new Error(e?.detail || `Bridge failed (${br.status})`); }
    toast.success("Added to planner (In Design) — opening editor…", { id: tId });
    onMutated && onMutated();
    onOpenChange && onOpenChange(false);
    onOpenCard && onOpenCard(card.card_id);
  } catch (e) {
    toast.error(`Couldn't open in editor: ${e.message}`, { id: tId });
  } finally {
    setBusy && setBusy(false);
  }
}

function CoverLibraryVariantTile({ runId, side, variantIndex, onChanged, boardCtx }) {
  const [downloading, setDownloading] = useState(false);
  const [composing, setComposing] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeTitle, setComposeTitle] = useState("");
  const [composeSubtitle, setComposeSubtitle] = useState("");
  const [clientLogo, setClientLogo] = useState(null);
  const [composedUrls, setComposedUrls] = useState(null);
  const [savingBg, setSavingBg] = useState(false);
  const [editingBoard, setEditingBoard] = useState(false);
  const previewSrc = `${API}/v2/cover-design/runs/${runId}/preview/${side}/${variantIndex}`;
  const thumbSrc = `${previewSrc}?thumb=1`;
  const downloadSrc = `${API}/v2/cover-design/runs/${runId}/file/${side}/${variantIndex}`;

  const saveBackground = async () => {
    const name = window.prompt("Name this background (e.g. 'Gov blue waves'):", "");
    if (name === null) return;
    if (!name.trim()) { toast.error("A name is required"); return; }
    setSavingBg(true);
    try {
      const r = await fetch(`${API}/v2/cover-design/saved-backgrounds/from-variant`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ run_id: runId, side, variant_index: variantIndex, name: name.trim() }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success("Saved to your backgrounds library ⭐");
    } catch (e) {
      toast.error(`Save failed: ${e.message}`);
    } finally {
      setSavingBg(false);
    }
  };

  const deleteVariant = async () => {
    if (!window.confirm("Delete this variant from the library?")) return;
    try {
      const r = await fetch(`${API}/v2/cover-design/runs/${runId}/variant/${side}/${variantIndex}`, {
        method: "DELETE", credentials: "include",
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success("Variant deleted");
      onChanged && onChanged();
    } catch (e) {
      toast.error(`Delete failed: ${e.message}`);
    }
  };

  const handleDownload = async (e, url, filename) => {
    e.preventDefault();
    e.stopPropagation();
    setDownloading(true);
    try {
      const resp = await fetch(url, { credentials: "include" });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      toast.success(`Downloaded ${filename}`);
    } catch (err) {
      console.error("download failed", err);
      toast.error(`Download failed: ${err.message}`);
    } finally {
      setDownloading(false);
    }
  };

  const handleCompose = async () => {
    if (composeTitle.trim().length < 1) {
      toast.error("Title is required");
      return;
    }
    setComposing(true);
    try {
      const form = new FormData();
      form.append("title", composeTitle.trim());
      form.append("subtitle", composeSubtitle.trim());
      if (clientLogo) form.append("client_logo", clientLogo);
      const resp = await fetch(
        `${API}/v2/cover-design/runs/${runId}/compose-template-a/${side}/${variantIndex}`,
        { method: "POST", credentials: "include", body: form }
      );
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err?.detail || `HTTP ${resp.status}`);
      }
      const body = await resp.json();
      const root = API.replace(/\/api$/, "");
      setComposedUrls({
        png: `${root}${body.png_url}`,
        pdf: `${root}${body.pdf_url}`,
      });
      toast.success(`Template A composed`);
    } catch (err) {
      console.error("compose failed", err);
      toast.error(`Compose failed: ${err.message}`);
    } finally {
      setComposing(false);
    }
  };

  return (
    <div className="space-y-1" data-testid={`cover-library-variant-${side}-${variantIndex}-${runId}`}>
      {/* NOTE: this thumbnail used to be an <a href={previewSrc} target="_blank">.
          The global "Hide Emergent badge" rule in index.css includes
          a[href*="emergentagent"] { display:none !important }, and because the
          backend lives on the *.preview.emergentagent.com domain, that selector
          matched the preview anchor and collapsed the whole tile to zero height
          (the <img> loaded fine — its parent <a> was hidden). Using a <button>
          + window.open keeps the click-to-open-full-preview behaviour without
          tripping the badge selector. Do NOT revert to an <a href>. */}
      <button
        type="button"
        onClick={() => window.open(previewSrc, "_blank", "noopener,noreferrer")}
        className="block w-full"
        title="Open full-size preview"
        data-testid={`cover-library-preview-open-${side}-${variantIndex}-${runId}`}
      >
        <img
          src={thumbSrc}
          loading="lazy"
          alt={`${side} variant ${variantIndex}`}
          className="w-full h-24 object-cover rounded border border-slate-200 hover:opacity-90 transition-opacity"
          onError={(e) => {
            e.currentTarget.style.display = "none";
            const fallback = e.currentTarget.nextElementSibling;
            if (fallback) fallback.style.display = "flex";
          }}
        />
        <div
          className="w-full h-24 bg-slate-100 rounded border border-slate-200 items-center justify-center text-[10px] text-slate-500"
          style={{ display: "none" }}
        >
          Preview unavailable
        </div>
      </button>
      <button
        type="button"
        onClick={(e) => handleDownload(e, downloadSrc, `cover_bg_${runId}_${side}_${variantIndex}.png`)}
        disabled={downloading}
        className="block w-full text-center text-[10px] bg-slate-700 hover:bg-slate-800 disabled:opacity-50 text-white rounded px-2 py-1"
        data-testid={`cover-library-download-${side}-${variantIndex}-${runId}`}
      >
        {downloading
          ? <Loader2 className="w-3 h-3 inline animate-spin" />
          : <><Download className="w-3 h-3 inline mr-0.5" />bg v{variantIndex}</>}
      </button>
      <div className="flex gap-1">
        <button
          type="button"
          onClick={saveBackground}
          disabled={savingBg}
          className="flex-1 text-center text-[10px] bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white rounded px-1 py-1 flex items-center justify-center gap-0.5"
          data-testid={`cover-library-save-${side}-${variantIndex}-${runId}`}
          title="Save this background to the reusable library"
        >
          {savingBg ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Star className="w-3 h-3" />Save</>}
        </button>
        <button
          type="button"
          onClick={deleteVariant}
          className="flex-1 text-center text-[10px] bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 rounded px-1 py-1 flex items-center justify-center gap-0.5"
          data-testid={`cover-library-delete-variant-${side}-${variantIndex}-${runId}`}
          title="Delete this variant"
        >
          <Trash2 className="w-3 h-3" />Del
        </button>
      </div>
      <button
        type="button"
        onClick={() => sendCoverToBoard({
          boardCtx,
          title: `Cover — ${side} v${variantIndex}`,
          bridge: { run_id: runId, side, variant_index: variantIndex, format: "a4_portrait" },
          setBusy: setEditingBoard,
        })}
        disabled={editingBoard}
        className="block w-full text-center text-[10px] bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white rounded px-2 py-1 flex items-center justify-center gap-1"
        data-testid={`cover-library-edit-board-${side}-${variantIndex}-${runId}`}
        title="Send to the planner (In Design) and open the full editor to move text & logos"
      >
        {editingBoard ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />} Edit on board
      </button>
      <button
        type="button"
        onClick={() => setComposeOpen(!composeOpen)}
        className="block w-full text-center text-[10px] bg-indigo-600 hover:bg-indigo-700 text-white rounded px-2 py-1"
        data-testid={`cover-library-compose-${side}-${variantIndex}-${runId}`}
      >
        {composeOpen ? "Hide compose" : "Compose Template A"}
      </button>
      {composeOpen && (
        <div className="space-y-1 p-2 bg-slate-50 border border-slate-200 rounded text-[10px]">
          <input
            type="text"
            placeholder="Title (required)"
            value={composeTitle}
            onChange={(e) => setComposeTitle(e.target.value)}
            className="w-full px-1 py-0.5 border border-slate-300 rounded text-[10px]"
            data-testid={`compose-title-input-${side}-${variantIndex}-${runId}`}
          />
          <input
            type="text"
            placeholder="Subtitle (optional)"
            value={composeSubtitle}
            onChange={(e) => setComposeSubtitle(e.target.value)}
            className="w-full px-1 py-0.5 border border-slate-300 rounded text-[10px]"
            data-testid={`compose-subtitle-input-${side}-${variantIndex}-${runId}`}
          />
          <label className="block">
            <span className="text-[9px] text-slate-600 uppercase tracking-wide">Client logo (optional, PNG/SVG)</span>
            <input
              type="file"
              accept="image/png,image/jpeg,image/svg+xml,image/webp"
              onChange={(e) => setClientLogo(e.target.files?.[0] || null)}
              className="w-full text-[10px] mt-0.5"
              data-testid={`compose-client-logo-input-${side}-${variantIndex}-${runId}`}
            />
            {clientLogo && (
              <span className="text-[9px] text-emerald-700 block mt-0.5">
                {clientLogo.name} ({Math.round(clientLogo.size / 1024)} KB)
              </span>
            )}
          </label>
          <button
            type="button"
            onClick={handleCompose}
            disabled={composing || composeTitle.trim().length < 1}
            className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded px-2 py-1 text-[10px]"
            data-testid={`compose-submit-${side}-${variantIndex}-${runId}`}
          >
            {composing ? <Loader2 className="w-3 h-3 inline animate-spin" /> : "Compose"}
          </button>
          {composedUrls && (
            <div className="space-y-1 pt-1 border-t border-slate-200" data-testid={`compose-result-${side}-${variantIndex}-${runId}`}>
              <button
                type="button"
                onClick={() => window.open(composedUrls.png, "_blank", "noopener,noreferrer")}
                className="block w-full"
                title="Open composed cover full-size"
              >
                <img
                  src={composedUrls.png}
                  alt={`Composed ${side} cover with title and logo`}
                  className="w-full max-h-72 object-contain rounded border border-emerald-300 bg-white hover:opacity-90 transition-opacity"
                  data-testid={`compose-preview-img-${side}-${variantIndex}-${runId}`}
                />
              </button>
              <p className="text-[9px] text-emerald-700 text-center">Composed — click to enlarge, or download below.</p>
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={(e) => handleDownload(e, composedUrls.png, `resiligent_cover_${runId}_${side}_${variantIndex}.png`)}
                  className="flex-1 bg-slate-900 hover:bg-slate-800 text-white rounded px-1 py-0.5 text-[10px]"
                  data-testid={`compose-download-png-${side}-${variantIndex}-${runId}`}
                >
                  <Download className="w-3 h-3 inline mr-0.5" />PNG
                </button>
                <button
                  type="button"
                  onClick={(e) => handleDownload(e, composedUrls.pdf, `resiligent_cover_${runId}_${side}_${variantIndex}.pdf`)}
                  className="flex-1 bg-slate-900 hover:bg-slate-800 text-white rounded px-1 py-0.5 text-[10px]"
                  data-testid={`compose-download-pdf-${side}-${variantIndex}-${runId}`}
                >
                  <Download className="w-3 h-3 inline mr-0.5" />PDF
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Saved Backgrounds tab — reusable library of front/back backgrounds ──
function SavedBackgroundsTab({ boardCtx }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all"); // all | front | back
  const [uploadName, setUploadName] = useState("");
  const [uploadSide, setUploadSide] = useState("front");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = filter === "all" ? "" : `?side=${filter}`;
      const r = await fetch(`${API}/v2/cover-design/saved-backgrounds${qs}`, { credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setItems(j.backgrounds || []);
    } catch (e) {
      toast.error(`Failed to load saved backgrounds: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const doUpload = async () => {
    if (!uploadFile) { toast.error("Choose an image file"); return; }
    if (!uploadName.trim()) { toast.error("Give it a name"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", uploadFile);
      fd.append("side", uploadSide);
      fd.append("name", uploadName.trim());
      const r = await fetch(`${API}/v2/cover-design/saved-backgrounds/upload`, {
        method: "POST", credentials: "include", body: fd,
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e?.detail || `HTTP ${r.status}`); }
      toast.success("Background uploaded to library ⭐");
      setUploadName(""); setUploadFile(null);
      load();
    } catch (e) {
      toast.error(`Upload failed: ${e.message}`);
    } finally {
      setUploading(false);
    }
  };

  const root = API.replace(/\/api$/, "");
  return (
    <div className="mt-4 space-y-4" data-testid="saved-backgrounds-tab">
      {/* Upload designer-made background */}
      <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2">
        <div className="text-xs font-semibold text-slate-700 flex items-center gap-1">
          <Upload className="h-3.5 w-3.5" /> Upload a designer background
        </div>
        <div className="flex gap-2">
          <input
            type="text" placeholder="Name (e.g. Navy minimal back)"
            value={uploadName} onChange={(e) => setUploadName(e.target.value)}
            className="flex-1 px-2 py-1 border border-slate-300 rounded text-xs"
            data-testid="saved-bg-upload-name"
          />
          <select
            value={uploadSide} onChange={(e) => setUploadSide(e.target.value)}
            className="px-2 py-1 border border-slate-300 rounded text-xs bg-white"
            data-testid="saved-bg-upload-side"
          >
            <option value="front">Front</option>
            <option value="back">Back</option>
          </select>
        </div>
        <input
          type="file" accept="image/png,image/jpeg,image/webp"
          onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
          className="w-full text-xs"
          data-testid="saved-bg-upload-file"
        />
        <Button size="sm" onClick={doUpload} disabled={uploading} className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="saved-bg-upload-btn">
          {uploading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Upload className="h-3 w-3 mr-1" />}
          Upload to library
        </Button>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-1">
        {["all", "front", "back"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`text-[11px] px-2 py-1 rounded border capitalize ${filter === f ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-300"}`}
            data-testid={`saved-bg-filter-${f}`}
          >
            {f}
          </button>
        ))}
        <button onClick={load} className="ml-auto text-[11px] text-slate-500 flex items-center gap-1" data-testid="saved-bg-refresh">
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="text-xs text-slate-500 flex items-center gap-2 py-6 justify-center"><Loader2 className="h-3 w-3 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-slate-500 italic text-center py-10" data-testid="saved-bg-empty">
          No saved backgrounds yet. Star a generated variant or upload one above.
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3" data-testid="saved-bg-grid">
          {items.map((b) => (
            <SavedBackgroundCard key={b.background_id} bg={b} root={root} onChanged={load} boardCtx={boardCtx} />
          ))}
        </div>
      )}
    </div>
  );
}

function SavedBackgroundCard({ bg, root, onChanged, boardCtx }) {
  const [composeOpen, setComposeOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [logo, setLogo] = useState(null);
  const [composing, setComposing] = useState(false);
  const [composed, setComposed] = useState(null);
  const [editingBoard, setEditingBoard] = useState(false);
  const previewSrc = `${root}${bg.preview_url}`;
  const thumbSrc = `${previewSrc}?thumb=1`;

  const remove = async () => {
    if (!window.confirm(`Delete saved background "${bg.name}"?`)) return;
    try {
      const r = await fetch(`${API}/v2/cover-design/saved-backgrounds/${bg.background_id}`, { method: "DELETE", credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success("Deleted");
      onChanged && onChanged();
    } catch (e) {
      toast.error(`Delete failed: ${e.message}`);
    }
  };

  const compose = async () => {
    if (!title.trim()) { toast.error("Title is required"); return; }
    setComposing(true);
    try {
      const fd = new FormData();
      fd.append("title", title.trim());
      fd.append("subtitle", subtitle.trim());
      if (logo) fd.append("client_logo", logo);
      const r = await fetch(`${API}/v2/cover-design/saved-backgrounds/${bg.background_id}/compose-template-a`, {
        method: "POST", credentials: "include", body: fd,
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e?.detail || `HTTP ${r.status}`); }
      const body = await r.json();
      setComposed({ png: `${root}${body.png_url}`, pdf: `${root}${body.pdf_url}` });
      toast.success("Composed cover ready");
    } catch (e) {
      toast.error(`Compose failed: ${e.message}`);
    } finally {
      setComposing(false);
    }
  };

  return (
    <div className="rounded-md border border-slate-200 bg-white p-2 space-y-1" data-testid={`saved-bg-card-${bg.background_id}`}>
      <button type="button" onClick={() => window.open(previewSrc, "_blank", "noopener,noreferrer")} className="block w-full">
        <img src={thumbSrc} loading="lazy" alt={bg.name} className="w-full h-28 object-cover rounded border border-slate-200 hover:opacity-90" data-testid={`saved-bg-img-${bg.background_id}`} />
      </button>
      <div className="flex items-center justify-between gap-1">
        <span className="text-[11px] font-medium text-slate-800 truncate" title={bg.name}>{bg.name}</span>
        <span className="text-[9px] uppercase tracking-wide text-slate-400">{bg.side}{bg.source === "uploaded" ? " · up" : ""}</span>
      </div>
      <div className="flex gap-1">
        <button type="button" onClick={() => blobDownload(`${root}${bg.download_url}`, `background_${bg.name || bg.background_id}.png`)} className="flex-1 text-center text-[10px] bg-slate-700 hover:bg-slate-800 text-white rounded px-1 py-1" data-testid={`saved-bg-download-${bg.background_id}`}>
          <Download className="w-3 h-3 inline mr-0.5" />PNG
        </button>
        <button type="button" onClick={() => setComposeOpen(!composeOpen)} className="flex-1 text-center text-[10px] bg-indigo-600 hover:bg-indigo-700 text-white rounded px-1 py-1" data-testid={`saved-bg-compose-toggle-${bg.background_id}`}>
          <Wand2 className="w-3 h-3 inline mr-0.5" />{composeOpen ? "Hide" : "Add text"}
        </button>
        <button type="button" onClick={remove} className="text-[10px] bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 rounded px-1.5 py-1" data-testid={`saved-bg-delete-${bg.background_id}`}>
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
      <button
        type="button"
        onClick={() => sendCoverToBoard({
          boardCtx,
          title: bg.name || `Cover — ${bg.side}`,
          bridge: { saved_background_id: bg.background_id, side: bg.side, format: "a4_portrait" },
          setBusy: setEditingBoard,
        })}
        disabled={editingBoard}
        className="block w-full text-center text-[10px] bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white rounded px-2 py-1 flex items-center justify-center gap-1"
        data-testid={`saved-bg-edit-board-${bg.background_id}`}
        title="Send to the planner (In Design) and open the full editor to move text & logos"
      >
        {editingBoard ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />} Edit on board
      </button>
      {composeOpen && (
        <div className="space-y-1 p-2 bg-slate-50 border border-slate-200 rounded">
          <input type="text" placeholder="Title (required)" value={title} onChange={(e) => setTitle(e.target.value)} className="w-full px-1 py-0.5 border border-slate-300 rounded text-[10px]" data-testid={`saved-bg-title-${bg.background_id}`} />
          <input type="text" placeholder="Subtitle (optional)" value={subtitle} onChange={(e) => setSubtitle(e.target.value)} className="w-full px-1 py-0.5 border border-slate-300 rounded text-[10px]" data-testid={`saved-bg-subtitle-${bg.background_id}`} />
          <input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" onChange={(e) => setLogo(e.target.files?.[0] || null)} className="w-full text-[10px]" data-testid={`saved-bg-logo-${bg.background_id}`} />
          <button type="button" onClick={compose} disabled={composing || !title.trim()} className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded px-2 py-1 text-[10px]" data-testid={`saved-bg-compose-submit-${bg.background_id}`}>
            {composing ? <Loader2 className="w-3 h-3 inline animate-spin" /> : "Compose"}
          </button>
          {composed && (
            <div className="space-y-1 pt-1 border-t border-slate-200">
              <img src={composed.png} alt="composed" className="w-full max-h-48 object-contain rounded border border-emerald-300" data-testid={`saved-bg-composed-img-${bg.background_id}`} />
              <div className="flex gap-1">
                <button type="button" onClick={() => blobDownload(composed.png, `cover_${bg.name || bg.background_id}.png`)} className="flex-1 text-center bg-slate-900 text-white rounded px-1 py-0.5 text-[10px]" data-testid={`saved-bg-composed-png-${bg.background_id}`}><Download className="w-3 h-3 inline mr-0.5" />PNG</button>
                <button type="button" onClick={() => blobDownload(composed.pdf, `cover_${bg.name || bg.background_id}.pdf`)} className="flex-1 text-center bg-slate-900 text-white rounded px-1 py-0.5 text-[10px]" data-testid={`saved-bg-composed-pdf-${bg.background_id}`}><Download className="w-3 h-3 inline mr-0.5" />PDF</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Programmatic blob download with credentials — avoids `<a href>` which the
// global badge-hiding CSS (a[href*="emergentagent"]) collapses in preview.
async function blobDownload(url, filename) {
  try {
    const resp = await fetch(url, { credentials: "include" });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const blob = await resp.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    toast.success(`Downloaded ${filename}`);
  } catch (e) {
    toast.error(`Download failed: ${e.message}`);
  }
}

// Re-export for convenience: the button that opens the drawer, used by
// ProductionPlanner alongside the Draft-with-Rex button.
export function OpenCoverLibraryButton({ onClick, className = "" }) {
  return (
    <Button
      onClick={onClick}
      variant="outline"
      className={className}
      data-testid="open-cover-library-btn"
    >
      <FileImage className="h-4 w-4 mr-2" />
      Cover Library
    </Button>
  );
}

// Quick-launch helper: re-exports Wand2 icon for callers that want to
// nudge people toward generating a new cover from the library when
// it's empty.
export { Wand2 as NewCoverIcon };
